import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Rocket, Users, Zap, Shield, Clock } from 'lucide-react';
import { getApiBaseUrl } from '@/utils/apiBase';

const JoinWaitlist: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    company: '',
    companySize: '',
    useCase: '',
    source: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to join waitlist');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#000000]">
        <div className="section-card max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-[#F5F5F7] mb-3">
            You're on the List!
          </h1>
          <p className="text-[#86868B] mb-6">
            Thanks for your interest in CoTaskr. We've received your request and will review it shortly. You'll receive an email once you're approved.
          </p>

          <Link to="/login" className="btn-t btn-t-primary inline-block">
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000] text-[#F5F5F7]">
      {/* Header */}
      <header className="border-b border-[#2C2C2E] p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative w-8 h-8">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full transform translate-x-0.5 translate-y-0.5 opacity-20"></div>
              <div className="relative w-8 h-8 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <span className="text-xl font-bold bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 bg-clip-text text-transparent">
              CoTaskr
            </span>
          </div>
          <Link to="/login" className="text-sm text-[#86868B] hover:text-[#F5F5F7] transition-colors">
            Already have an account? <span className="text-blue-400">Sign In</span>
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 py-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Column - Value Proposition */}
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-500 bg-clip-text text-transparent">
                AI-Powered
              </span>
              <br />
              Project Management
            </h1>
            <p className="text-lg text-[#86868B] mb-8">
              Join CoTaskr and transform how your team collaborates. Built for modern teams who want to move fast without losing control.
            </p>

            <div className="space-y-4 mb-8">
              {[
                { icon: Zap, text: 'AI-powered task generation and insights' },
                { icon: Users, text: 'Real-time collaboration for distributed teams' },
                { icon: Shield, text: 'Enterprise-grade security and compliance' },
                { icon: Clock, text: 'Smart time tracking and reporting' },
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-[#F5F5F7]">{feature.text}</span>
                </div>
              ))}
            </div>

            <div className="p-4 bg-[#1C1C1E] rounded-lg border border-[#2C2C2E]">
              <p className="text-sm text-[#86868B]">
                <Rocket className="w-4 h-4 inline-block mr-2 text-orange-400" />
                We're currently in private beta. Join the waitlist to get early access.
              </p>
            </div>
          </div>

          {/* Right Column - Signup Form */}
          <div className="section-card">
            <h2 className="text-2xl font-bold text-[#F5F5F7] mb-6">Join the Waitlist</h2>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  className="co-taskr-input"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="co-taskr-input"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="co-taskr-input"
                  placeholder="Acme Inc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                  Company Size
                </label>
                <select
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  className="co-taskr-input"
                >
                  <option value="">Select...</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="501+">501+ employees</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                  What will you use CoTaskr for?
                </label>
                <textarea
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleChange}
                  rows={3}
                  className="co-taskr-input"
                  placeholder="Tell us about your use case..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F5F5F7] mb-2">
                  How did you hear about us?
                </label>
                <select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className="co-taskr-input"
                >
                  <option value="">Select...</option>
                  <option value="Search">Search Engine</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Referral">Friend/Colleague Referral</option>
                  <option value="Blog">Blog/Article</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-t btn-t-primary"
              >
                {loading ? 'Submitting...' : 'Join Waitlist'}
              </button>

              <p className="text-xs text-[#86868B] text-center">
                By joining, you agree to receive updates about CoTaskr. We'll never spam you.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinWaitlist;
